import * as React from "react"
import { ChevronDown } from "lucide-react"
import { cn } from "../../lib/utils"

interface AccordionContextValue {
  openItems: Set<string>
  toggleItem: (value: string) => void
}

const AccordionContext = React.createContext<AccordionContextValue | undefined>(undefined)
const AccordionItemContext = React.createContext<{ value: string } | undefined>(undefined)

interface AccordionProps {
  children: React.ReactNode
  type?: "single" | "multiple"
  defaultValue?: string | string[]
  className?: string
}

export const Accordion = React.forwardRef<HTMLDivElement, AccordionProps>(
  ({ children, type = "single", defaultValue, className, ...props }, ref) => {
    const [openItems, setOpenItems] = React.useState<Set<string>>(() => {
      if (!defaultValue) return new Set()
      if (Array.isArray(defaultValue)) return new Set(defaultValue)
      return new Set([defaultValue])
    })

    const toggleItem = React.useCallback((value: string) => {
      setOpenItems((prev) => {
        const next = new Set(prev)
        if (next.has(value)) {
          next.delete(value)
        } else {
          if (type === "single") {
            next.clear()
          }
          next.add(value)
        }
        return next
      })
    }, [type])
    const contextValue = React.useMemo(() => ({ openItems, toggleItem }), [openItems, toggleItem])

    return (
      <AccordionContext.Provider value={contextValue}>
        <div ref={ref} className={cn("w-full", className)} {...props}>
          {children}
        </div>
      </AccordionContext.Provider>
    )
  }
)
Accordion.displayName = "Accordion"

interface AccordionItemProps {
  value: string
  children: React.ReactNode
  className?: string
}

export const AccordionItem = React.forwardRef<HTMLDivElement, AccordionItemProps>(
  ({ value, children, className, ...props }, ref) => {
    const context = React.useContext(AccordionContext)
    if (!context) {
      throw new Error("AccordionItem must be used within Accordion")
    }
    const itemContextValue = React.useMemo(() => ({ value }), [value])

    return (
      <AccordionItemContext.Provider value={itemContextValue}>
        <div
          ref={ref}
          className={cn("border-b", className)}
          {...props}
        >
          {children}
        </div>
      </AccordionItemContext.Provider>
    )
  }
)
AccordionItem.displayName = "AccordionItem"

interface AccordionTriggerProps {
  children: React.ReactNode
  className?: string
}

export const AccordionTrigger = React.forwardRef<HTMLButtonElement, AccordionTriggerProps>(
  ({ children, className, ...props }, ref) => {
    const context = React.useContext(AccordionContext)
    const itemContext = React.useContext(AccordionItemContext)
    
    if (!context || !itemContext) {
      throw new Error("AccordionTrigger must be used within AccordionItem")
    }

    const isOpen = context.openItems.has(itemContext.value)

    return (
      <button
        ref={ref}
        type="button"
        onClick={() => context.toggleItem(itemContext.value)}
        className={cn(
          // Aufgeklappt heißt nicht ausgewählt: den Zustand zeigt der Pfeil. Eine
          // Füllung würde mit der Markierung der aktiven Seite konkurrieren.
          "focus-glow flex w-full items-center justify-between rounded-md px-3 py-2 text-sm font-medium transition-all duration-fast ease-standard hover:bg-muted",
          className
        )}
        {...props}
      >
        {children}
        <ChevronDown
          className={cn(
            "h-4 w-4 text-muted-foreground transition-transform duration-200",
            isOpen && "rotate-180"
          )}
        />
      </button>
    )
  }
)
AccordionTrigger.displayName = "AccordionTrigger"

interface AccordionContentProps {
  children: React.ReactNode
  className?: string
}

export const AccordionContent = React.forwardRef<HTMLDivElement, AccordionContentProps>(
  ({ children, className, ...props }, ref) => {
    const context = React.useContext(AccordionContext)
    const itemContext = React.useContext(AccordionItemContext)
    const contentRef = React.useRef<HTMLDivElement | null>(null)
    const [contentHeight, setContentHeight] = React.useState(0)
    
    if (!context || !itemContext) {
      throw new Error("AccordionContent must be used within AccordionItem")
    }

    const isOpen = context.openItems.has(itemContext.value)
    React.useLayoutEffect(() => {
      if (!contentRef.current) return
      const updateHeight = () => setContentHeight(contentRef.current?.scrollHeight ?? 0)
      updateHeight()
      const observer = new ResizeObserver(updateHeight)
      observer.observe(contentRef.current)
      return () => observer.disconnect()
    }, [children])

    return (
      <div
        ref={ref}
        className={cn(
          "overflow-hidden transition-all duration-normal ease-standard motion-reduce:transition-none",
          isOpen ? "opacity-100" : "opacity-0"
        )}
        style={{ maxHeight: isOpen ? `${contentHeight}px` : '0px' }}
        {...props}
      >
        <div ref={contentRef} className={cn("py-1", className)}>
          {children}
        </div>
      </div>
    )
  }
)
AccordionContent.displayName = "AccordionContent"

